import { useMemo } from 'react'

import { useQuery } from '@api/hooks'
import { Box, Divider, Paper, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material'
import DirectDealApi, { DirectDealKeys } from '@services/directDeals.api'
import { DirectDealStatus, DirectDealSummary } from '@services/directDeals.types'
import { DirectDealBasket } from '@services/marketBasketReports.types'
import { NumberParam, StringParam, useQueryParam, withDefault } from 'use-query-params'

import DirectDealCard from './DirectDealCard'

type TabValue = 'ALL' | 'ACCEPTED' | 'REJECTED'

const statusMap: Record<TabValue, DirectDealStatus> = {
	ALL: DirectDealStatus.ALL,
	ACCEPTED: DirectDealStatus.ACCEPTED,
	REJECTED: DirectDealStatus.REJECTED,
}

type Props = {
	selectedBasket: DirectDealBasket | null
}

export default function DirectDealDetail({ selectedBasket }: Props) {
	const [selectedBasketId] = useQueryParam('basket', NumberParam, {
		updateType: 'replaceIn',
	})
	const [activeTab, setActiveTab] = useQueryParam('direct-deals', withDefault(StringParam, 'ALL'), {
		updateType: 'replaceIn',
	})

	const { data: summary, isLoading: isSummaryLoading } = useQuery({
		queryKey: DirectDealKeys.summary(selectedBasketId),
		queryFn: () => DirectDealApi.summary(selectedBasketId),
		enabled: !!selectedBasketId,
	})

	const { data: deals, isLoading: isDealsLoading } = useQuery({
		queryKey: DirectDealKeys.list(selectedBasketId, { status: statusMap[activeTab] }),
		queryFn: () => DirectDealApi.list(selectedBasketId, { status: statusMap[activeTab] }),
		enabled: !!selectedBasketId,
	})

	const counts = useMemo(
		() => ({
			all: summary?.total ?? 0,
			accepted: summary?.accepted ?? 0,
			rejected: summary?.rejected ?? 0,
		}),
		[summary]
	)

	const isLoading = isSummaryLoading || isDealsLoading

	return (
		<DirectDealDetailUI
			basket={selectedBasket}
			deals={deals ?? null}
			counts={counts}
			activeTab={activeTab as TabValue}
			onTabChange={setActiveTab}
			isLoading={isLoading}
		/>
	)
}

type UiProps = {
	basket: DirectDealBasket | null
	deals: DirectDealSummary[] | null
	counts: { all: number; accepted: number; rejected: number }
	activeTab: TabValue
	onTabChange: (val: TabValue) => void
	isLoading?: boolean
}

export function DirectDealDetailUI({
	basket,
	deals,
	counts,
	activeTab,
	onTabChange,
	isLoading = false,
}: UiProps) {
	return (
		<Paper
			sx={{
				flex: 1,
				height: '100%',
				minHeight: 0,
				minWidth: 0,
				border: '1px solid',
				borderColor: 'divider',
				borderRadius: 2,
				p: 2,
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
				overflow: 'hidden',
			}}
		>
			<Stack direction="row" alignItems="baseline" justifyContent="space-between">
				<Typography variant="h5">{basket?.displayName}</Typography>
				<Typography variant="body1" fontWeight={600} color="text.secondary">
					{basket?.salesforceCaseId}
				</Typography>
			</Stack>

			<Divider />

			<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
				<Tabs value={activeTab} onChange={(_, val) => onTabChange(val)}>
					<Tab label={`All (${counts.all})`} value="ALL" data-testid="tab-detail-all" />
					<Tab
						label={`Accepted (${counts.accepted})`}
						value="ACCEPTED"
						data-testid="tab-detail-accepted"
					/>
					<Tab
						label={`Rejected (${counts.rejected})`}
						value="REJECTED"
						data-testid="tab-detail-rejected"
					/>
				</Tabs>
			</Box>

			<Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
				<Stack spacing={1}>
					{isLoading ? (
						<Stack spacing={1}>
							<Skeleton variant="rounded" width="100%" height="8.625rem" />
							<Skeleton variant="rounded" width="100%" height="8.625rem" />
							<Skeleton variant="rounded" width="100%" height="8.625rem" />
						</Stack>
					) : (
						deals?.map((deal) => (
							<DirectDealCard
								key={deal.id}
								deal={deal}
								data-testid={`direct-deal-card-${deal.id}`}
							/>
						))
					)}
				</Stack>
			</Box>
		</Paper>
	)
}
